import { useState } from 'react';
import { useResoudreHubspot } from '../api/hooks';
import type { CommandeListee, EtatIntegration, ResolutionHubspot } from '../api/types';
import { useToasts } from './Toasts';

/**
 * Style d'un texte rendu cliquable.
 *
 * Les liens ne sont plus des icônes à côté de la donnée, mais la donnée
 * elle-même : le nom du client ouvre sa fiche, la date d'expédition ouvre le
 * suivi, la référence ouvre la commande chez Shopify ou Sellsy. Une icône
 * demandait de deviner sur quoi elle portait ; le texte souligné, non.
 */
const LIEN: React.CSSProperties = {
  background: 'none',
  border: 0,
  padding: 0,
  font: 'inherit',
  color: 'inherit',
  textAlign: 'left',
  textDecoration: 'underline',
  textDecorationStyle: 'dotted',
  textUnderlineOffset: 3,
  cursor: 'pointer',
};

/**
 * Nom du client, cliquable vers sa fiche HubSpot.
 *
 * Trois états, et aucun ne masque le nom : lien ordinaire quand l'identifiant
 * est déjà connu, bouton qui le résout sinon, texte simple quand HubSpot ne
 * peut rien dire — avec la raison au survol. Un nom qui disparaîtrait faute de
 * lien serait une régression pour la lecture de la liste.
 */
export function LienClient({
  commande,
  hubspot,
  onDoublons,
  onLienBloque,
}: {
  commande: CommandeListee;
  hubspot: EtatIntegration | undefined;
  onDoublons: (r: Extract<ResolutionHubspot, { statut: 'doublons' }>) => void;
  /** Appelé quand le navigateur a bloqué la fenêtre : le lien reste accessible. */
  onLienBloque: (url: string, client: string) => void;
}) {
  const resoudre = useResoudreHubspot();
  const { ajouter } = useToasts();
  const [enCours, setEnCours] = useState(false);

  const indisponible = !commande.email
    ? 'Aucun email client sur cette commande'
    : hubspot && !hubspot.disponible
      ? (hubspot.raison ?? 'HubSpot est indisponible')
      : null;

  /**
   * Ouvre la fiche HubSpot.
   *
   * L'identifiant n'est pas connu du navigateur : il faut le demander à l'API,
   * qui interroge HubSpot avec le token privé. La fenêtre est donc ouverte
   * AVANT l'appel — ouverte après, le navigateur la bloquerait, n'y voyant plus
   * la conséquence directe d'un clic.
   *
   * `window.open` reçoit `about:blank` et AUCUNE option `noopener` : passer
   * `noopener` fait renvoyer `null` par la spécification, et on perd la
   * référence dont on a besoin pour naviguer. La même garantie s'obtient en
   * coupant `opener` juste après.
   */
  async function ouvrirHubspot() {
    if (!commande.email || enCours) return;

    const onglet = window.open('about:blank', '_blank');
    if (onglet) onglet.opener = null;

    setEnCours(true);
    try {
      const r = await resoudre.mutateAsync({ email: commande.email, commandeId: commande.id });

      if (r.statut === 'trouve') {
        if (onglet) onglet.location.replace(r.contact.url);
        // Fenêtre bloquée : on ne perd pas la résolution, on donne le lien.
        else onLienBloque(r.contact.url, commande.client);
        return;
      }

      onglet?.close();
      if (r.statut === 'doublons') { onDoublons(r); return; }
      if (r.statut === 'absent') {
        ajouter(`Aucun contact HubSpot pour ${commande.email}. À créer avant de pouvoir ouvrir sa fiche.`, 'bad');
        return;
      }
      ajouter(r.statut === 'indisponible' ? r.raison : "Cette commande n'a pas d'email client.", 'bad');
    } catch {
      onglet?.close();
      ajouter('La recherche HubSpot a échoué.', 'bad');
    } finally {
      setEnCours(false);
    }
  }

  if (indisponible) {
    return <span title={`Fiche HubSpot indisponible : ${indisponible}`}>{commande.client}</span>;
  }

  if (commande.liens.hubspot) {
    /* Identifiant déjà résolu : un lien ordinaire, sans appel ni fenêtre
       ouverte en JavaScript — donc rien qu'un bloqueur puisse arrêter. */
    return (
      <a
        href={commande.liens.hubspot} target="_blank" rel="noopener noreferrer"
        style={LIEN} onClick={(e) => e.stopPropagation()}
        title="Ouvrir la fiche client dans HubSpot"
      >
        {commande.client}
      </a>
    );
  }

  return (
    <button
      type="button" style={LIEN} disabled={enCours}
      onClick={(e) => { e.stopPropagation(); void ouvrirHubspot(); }}
      title="Ouvrir la fiche client dans HubSpot"
      aria-label={`Ouvrir la fiche HubSpot de ${commande.client}`}
    >
      {commande.client}
      {enCours && <span className="spin" style={{ marginLeft: 6 }} />}
    </button>
  );
}

/**
 * Date d'expédition, cliquable vers le suivi du colis.
 *
 * Sans URL connue, la date reste affichée en texte, avec la raison au survol :
 * ce n'est pas la date qui manque, c'est le lien.
 */
export function LienExpedition({
  commande, children,
}: {
  commande: CommandeListee;
  children: React.ReactNode;
}) {
  if (!commande.liens.suivi) {
    return (
      <span title={commande.liens.suiviIndisponible ?? 'Suivi indisponible'}>{children}</span>
    );
  }
  return (
    <a
      href={commande.liens.suivi} target="_blank" rel="noopener noreferrer"
      style={LIEN} onClick={(e) => e.stopPropagation()}
      title={`Suivre le colis chez ${commande.transporteur ?? 'le transporteur'}`}
      aria-label={`Suivre le colis ${commande.tracking ?? ''} chez ${commande.transporteur ?? 'le transporteur'}`}
    >
      {children}
    </a>
  );
}

const LIBELLE_BOUTIQUE = { shopify: 'Shopify', sellsy: 'Sellsy' } as const;

/**
 * Référence de commande, cliquable vers son outil d'origine.
 *
 * `null` quand la commande ne porte aucun identifiant — le cas de tous les
 * documents importés jusqu'ici. L'appelant affiche alors la référence en clair,
 * plutôt qu'un lien qui ne mènerait nulle part.
 */
export function LienBoutique({
  commande, children,
}: {
  commande: CommandeListee;
  children: React.ReactNode;
}) {
  const origine = commande.liens.boutiqueOrigine;
  if (!commande.liens.boutique || !origine) return null;

  return (
    <a
      href={commande.liens.boutique} target="_blank" rel="noopener noreferrer"
      className="mono" style={{ ...LIEN, fontWeight: 600 }}
      onClick={(e) => e.stopPropagation()}
      title={`Ouvrir ${commande.ref} dans ${LIBELLE_BOUTIQUE[origine]}`}
      aria-label={`Ouvrir la commande ${commande.ref} dans ${LIBELLE_BOUTIQUE[origine]}`}
    >
      {children}
    </a>
  );
}
